import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { AccountData, AuthContextType, Tokens } from "./types";
import config from "../config";

const FALLBACK_TOKEN_TTL_MS = 60 * 1000;
const REFRESH_SKEW_MS = 30 * 1000;

type TokenPayload = {
    access_token?: string;
    refresh_token?: string;
    expires_in?: number;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth muss innerhalb eines AuthProviders verwendet werden.");
    return ctx;
};

export function AuthProvider({ children }: { children: ReactNode }) {
    const [tokens, setTokens] = useState<Tokens | null>(() => {
        if (typeof window === "undefined") return null;
        const stored = window.localStorage.getItem("tokens");
        if (!stored) return null;
        try {
            const parsed = JSON.parse(stored) as Tokens | (Tokens & { expiresAt?: number });
            if (!parsed.expiresAt) {
                parsed.expiresAt = Date.now() + FALLBACK_TOKEN_TTL_MS;
            }
            return parsed;
        } catch {
            window.localStorage.removeItem("tokens");
            return null;
        }
    });
    const [account, setAccount] = useState<AccountData | null>(() => {
        if (typeof window === "undefined") return null;
        const stored = window.localStorage.getItem("account");
        return stored ? JSON.parse(stored) : null;
    });

    const isAuthenticated = !!tokens?.accessToken;

    const persistTokens = useCallback((payload: TokenPayload | null | undefined, fallbackRefreshToken?: string): Tokens | null => {
        if (!payload?.access_token) {
            console.warn("Token-Antwort enthält kein access_token und kann nicht gespeichert werden.");
            return null;
        }

        const refreshToken = payload.refresh_token ?? fallbackRefreshToken;
        if (!refreshToken) {
            console.warn("Token-Antwort enthält kein refresh_token und kein Fallback. Abbruch.");
            return null;
        }

        const expiresInMs = typeof payload.expires_in === "number" && payload.expires_in > 0
            ? payload.expires_in * 1000
            : FALLBACK_TOKEN_TTL_MS;

        const normalized: Tokens = {
            accessToken: payload.access_token,
            refreshToken,
            expiresAt: Date.now() + expiresInMs,
        };

        localStorage.setItem("tokens", JSON.stringify(normalized));
        setTokens(normalized);
        return normalized;
    }, []);

    /** Accountdaten separat laden */
    const reloadAccount = useCallback(async (tokenOverride?: string) => {
        const effectiveToken = tokenOverride ?? tokens?.accessToken;
        if (!effectiveToken) return;

        const res = await fetch(`${config.apiUrl}/api/accounts/me`, {
            headers: { Authorization: `Bearer ${effectiveToken}` },
        });

        if (!res.ok) {
            console.warn("Account daten konnten nicht geladen werden", res.status);
            return;
        }

        const acc = await res.json();
        localStorage.setItem("account", JSON.stringify(acc.content ?? acc));
        setAccount(acc.content ?? acc);
    }, [tokens?.accessToken]);

    /** Login gegen /api/login */
    const login = async (username: string, password: string, onSuccess?: () => void) => {
        const res = await fetch(`${config.apiUrl}/api/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username, password }),
        });

        if (!res.ok) throw new Error("Login fehlgeschlagen");

        const data = await res.json();
        const payload = (data?.content ?? data) as TokenPayload;
        const newTokens = persistTokens(payload, payload?.refresh_token);
        if (!newTokens) throw new Error("Login fehlgeschlagen (ungültige Token-Antwort)");

        await reloadAccount(newTokens.accessToken);

        if (onSuccess) onSuccess(); // ✅ Callback
    };


    const logout = useCallback(() => {
        localStorage.removeItem("tokens");
        localStorage.removeItem("account");
        setTokens(null);
        setAccount(null);
    }, []);

    /** Token Refresh über /api/login/refresh */
    const refresh = useCallback(async () => {
        if (!tokens?.refreshToken) return;
        const res = await fetch(`${config.apiUrl}/api/login/refresh`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refreshToken: tokens.refreshToken }),
        });

        if (res.ok) {
            const data = await res.json();
            const payload = (data?.content ?? data) as TokenPayload;
            const updated = persistTokens(payload, tokens.refreshToken);
            if (!updated) {
                logout();
            }
        } else {
            logout();
        }
    }, [logout, persistTokens, tokens?.refreshToken]);

    /** Tokens aktiv halten solange Seite sichtbar ist */
    useEffect(() => {
        if (typeof window === "undefined" || typeof document === "undefined") return;
        if (!tokens?.refreshToken || !tokens?.expiresAt) return;

        let refreshTimer: number | undefined;

        const scheduleRefresh = () => {
            const timeUntilRefresh = tokens.expiresAt - Date.now() - REFRESH_SKEW_MS;
            const delay = Math.max(timeUntilRefresh, 0);
            refreshTimer = window.setTimeout(triggerRefresh, delay);
        };

        const triggerRefresh = async () => {
            if (document.visibilityState !== "visible") {
                scheduleRefresh();
                return;
            }
            try {
                await refresh();
            } catch (error) {
                console.error("Automatischer Token-Refresh fehlgeschlagen", error);
            }
        };

        scheduleRefresh();

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                if (refreshTimer) {
                    window.clearTimeout(refreshTimer);
                }
                scheduleRefresh();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            if (refreshTimer) {
                window.clearTimeout(refreshTimer);
            }
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [tokens?.refreshToken, tokens?.expiresAt, refresh]);

    /** Wenn Token vorhanden, Account automatisch laden */
    useEffect(() => {
        if (tokens && !account) reloadAccount();
    }, [account, reloadAccount, tokens]);

    return (
        <AuthContext.Provider value={{ isAuthenticated, tokens, account, login, logout, refresh, reloadAccount }}>
            {children}
        </AuthContext.Provider>
    );
}
